import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { toast } from 'react-toastify';
import { PortalReportsApi } from '../../../api/portalReports';
import { buildLoadViewUrl, buildReplicateDraftUrl } from '../../../utils/savedViews';
import { LocalStorage } from '../../../utils/LocalStorage';
import { Spinner } from '../../Spinner';
import { FaIcon } from '../../FaIcon';
import { EditViewMetadataModal } from './EditViewMetadataModal';
import { YesNoModal } from '../../elements/YesNoModal';

const SCOPE_CLIENT = 'CLIENT';
const SCOPE_ALL = 'ALL';

/**
 * Modal para listar y cargar vistas guardadas (Card #2420).
 *
 * Dos scopes (control segmentado arriba):
 *  - "Este cliente": vistas del cliente actual, en dos secciones (ASIGNADAS AL PORTAL /
 *    MIS GUARDADAS). Click en la fila = Abrir.
 *  - "Todos los clientes": vistas de TODA la agencia agrupadas por cliente (el actual
 *    fijado arriba con "(actual)"). Cada vista de otro cliente expone [Abrir] +
 *    [Replicar aquí]. Replicar carga la estructura como borrador en el cliente actual.
 *
 * Keyboard-first (patrón command-palette): ↑/↓ navegan la lista visible, Enter = Abrir,
 * ⌘/Ctrl+Enter = Replicar aquí (si es de otro cliente). Se abre con ⌘K/Ctrl+K o el botón
 * del toolbar.
 */
class SavedViewsCommandPaletteComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Scope "Este cliente"
            loading: false,
            views: [],
            error: null,
            // Scope "Todos los clientes" (lazy)
            scope: SCOPE_CLIENT,
            allViews: [],
            allLoaded: false,
            allLoading: false,
            allError: null,
            // Comunes
            search: '',
            highlightIndex: 0,
            // Gestión desde el listado (P2): editar / eliminar una vista sin cargarla.
            editingView: null,
            deletingView: null,
            deleting: false,
        };
        this.inputRef = React.createRef();
        // Lista plana de vistas visibles (en orden de render) para la navegación por teclado.
        // Se recalcula en cada render; el handler de teclado la lee.
        this._flatViews = [];
    }

    componentDidUpdate(prevProps) {
        if (this.props.isOpen && !prevProps.isOpen) {
            // Reset al abrir: scope vuelve a "Este cliente" y se refrescan ambos listados.
            this.setState({
                search: '',
                error: null,
                scope: SCOPE_CLIENT,
                allViews: [],
                allLoaded: false,
                allError: null,
                highlightIndex: 0,
            });
            if (this.props.agencyClientId) {
                this.fetchViews();
            } else {
                this.setState({ views: [] });
            }
            setTimeout(() => {
                if (this.inputRef.current) this.inputRef.current.focus();
            }, 50);
        }
    }

    fetchViews() {
        const { t, agencyClientId } = this.props;
        this.setState({ loading: true, error: null });
        PortalReportsApi.listViews(agencyClientId)
            .then((response) => {
                if (!response || response.status === 'ERROR') {
                    this.setState({
                        loading: false,
                        views: [],
                        error: (response && response.error_message) || t('saved_views.load_error'),
                    });
                    return;
                }
                this.setState({
                    loading: false,
                    views: Array.isArray(response.views) ? response.views : [],
                });
            })
            .catch(() => {
                this.setState({ loading: false, views: [], error: t('saved_views.load_error') });
            });
    }

    fetchAllViews() {
        const { t } = this.props;
        this.setState({ allLoading: true, allError: null });
        PortalReportsApi.listAllViews()
            .then((response) => {
                if (!response || response.status === 'ERROR') {
                    this.setState({
                        allLoading: false,
                        allViews: [],
                        allLoaded: true,
                        allError: (response && response.error_message) || t('saved_views.load_error'),
                    });
                    return;
                }
                this.setState({
                    allLoading: false,
                    allLoaded: true,
                    allViews: Array.isArray(response.views) ? response.views : [],
                });
            })
            .catch(() => {
                this.setState({
                    allLoading: false,
                    allViews: [],
                    allLoaded: true,
                    allError: t('saved_views.load_error'),
                });
            });
    }

    setScope = (scope) => {
        this.setState({ scope, highlightIndex: 0 }, () => {
            if (scope === SCOPE_ALL && !this.state.allLoaded && !this.state.allLoading) {
                this.fetchAllViews();
            }
            if (this.inputRef.current) this.inputRef.current.focus();
        });
    };

    // Antes de navegar a propósito (window.location.href) apagamos el guard de "cambios sin
    // guardar" del Page para que el browser no muestre el aviso de "estás saliendo del sitio"
    // en una navegación intencional (abrir / replicar). (P4)
    _navigateTo(url) {
        if (this.props.onBeforeNavigateAway) this.props.onBeforeNavigateAway();
        window.location.href = url;
    }

    handleOpen = (view) => {
        const url = buildLoadViewUrl(view);
        if (!url) {
            toast.error(this.props.t('saved_views.load_error'));
            return;
        }
        this._navigateTo(url);
    };

    handleReplicate = (view) => {
        const { t, agencyClientId } = this.props;
        if (!agencyClientId) {
            toast.error(t('saved_views.replicate_needs_client'));
            return;
        }
        const url = buildReplicateDraftUrl(view, agencyClientId);
        if (!url) {
            toast.error(t('saved_views.load_error'));
            return;
        }
        // Las chart preferences (KPIs/barras/torta) no viajan en el blob de filtros: las
        // persistimos en LocalStorage para que los sub-componentes las lean al re-montar.
        if (view.config && view.config.chart_preferences) {
            LocalStorage.saveDataExplorerChartPreferences(view.config.chart_preferences);
        }
        this._navigateTo(url);
    };

    // ── Gestión desde el listado (P2) ────────────────────────────────────────────
    _refreshCurrentScope() {
        if (this.state.scope === SCOPE_ALL) this.fetchAllViews();
        else if (this.props.agencyClientId) this.fetchViews();
    }

    handleEditView = (view, e) => {
        if (e) e.stopPropagation();
        this.setState({ editingView: view });
    };

    handleAskDelete = (view, e) => {
        if (e) e.stopPropagation();
        this.setState({ deletingView: view });
    };

    confirmDelete = () => {
        const { t } = this.props;
        const view = this.state.deletingView;
        if (!view) return;
        this.setState({ deleting: true });
        PortalReportsApi.deleteView(view.id)
            .then((response) => {
                if (response && response.status === 'ERROR') {
                    toast.error((response && response.error_message) || t('saved_views.delete_error'));
                    this.setState({ deleting: false });
                    return;
                }
                toast.success(t('saved_views.deleted'));
                this.setState({ deleting: false, deletingView: null }, () => this._refreshCurrentScope());
            })
            .catch(() => {
                toast.error(t('saved_views.delete_error'));
                this.setState({ deleting: false });
            });
    };

    handleMetadataSaved = (updated) => {
        // Refrescamos el listado para reflejar el nuevo nombre/descripción.
        this.setState({ editingView: null }, () => this._refreshCurrentScope());
    };

    _isOtherClient(view) {
        const { agencyClientId } = this.props;
        return agencyClientId && String(view.agency_client_id) !== String(agencyClientId);
    }

    _matchesSearch(view) {
        const needle = this.state.search.trim().toLowerCase();
        if (!needle) return true;
        const name = (view.name || '').toLowerCase();
        const description = (view.description || '').toLowerCase();
        const clientName = (view.agency_client_name || '').toLowerCase();
        return name.includes(needle) || description.includes(needle) || clientName.includes(needle);
    }

    // Agrupa las vistas de "Todos los clientes" por cliente. El cliente actual va primero
    // (rotulado "(actual)"); el resto, alfabético por nombre de cliente.
    _buildAllGroups() {
        const { t, agencyClientId } = this.props;
        const filtered = this.state.allViews.filter((v) => this._matchesSearch(v));

        const byClient = new Map();
        filtered.forEach((view) => {
            const cid = String(view.agency_client_id);
            if (!byClient.has(cid)) {
                byClient.set(cid, {
                    clientId: cid,
                    clientName: view.agency_client_name || t('saved_views.unnamed_client'),
                    isCurrent: !!agencyClientId && cid === String(agencyClientId),
                    views: [],
                });
            }
            byClient.get(cid).views.push(view);
        });

        const groups = Array.from(byClient.values());
        groups.sort((a, b) => {
            if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
            return a.clientName.localeCompare(b.clientName);
        });
        return groups;
    }

    handleKeyDown = (e) => {
        const flat = this._flatViews;
        if (!flat || flat.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState((s) => ({ highlightIndex: Math.min(s.highlightIndex + 1, flat.length - 1) }));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState((s) => ({ highlightIndex: Math.max(s.highlightIndex - 1, 0) }));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const view = flat[Math.min(this.state.highlightIndex, flat.length - 1)];
            if (!view) return;
            if ((e.metaKey || e.ctrlKey) && this._isOtherClient(view)) {
                this.handleReplicate(view);
            } else {
                this.handleOpen(view);
            }
        }
    };

    _renderViewRow(view, flatIndex) {
        const { t, agencyClientId } = this.props;
        const isAssigned = view.assigned_to_portal;
        const isOther = this._isOtherClient(view);
        const highlighted = flatIndex === this.state.highlightIndex;

        return (
            <div
                key={`${view.agency_client_id}-${view.id}`}
                className={`saved-views-palette-row ${highlighted ? '-highlighted' : ''}`}
                onMouseEnter={() => this.setState({ highlightIndex: flatIndex })}
            >
                <button
                    type="button"
                    className="saved-views-palette-row-click"
                    onClick={() => this.handleOpen(view)}
                >
                    <div className="saved-views-palette-row-main">
                        {isAssigned && (
                            <span className="saved-view-portal-badge">
                                {t('saved_views.badge_in_portal')}
                            </span>
                        )}
                        <span className="saved-views-palette-row-name">{view.name}</span>
                    </div>
                    {view.description && (
                        <div className="saved-views-palette-row-desc">{view.description}</div>
                    )}
                </button>
                <div className="saved-views-palette-row-actions">
                    <button
                        type="button"
                        className="saved-views-palette-action -open"
                        onClick={() => this.handleOpen(view)}
                    >
                        {t('saved_views.action_open')}
                    </button>
                    {isOther && (
                        <button
                            type="button"
                            className="saved-views-palette-action -replicate"
                            onClick={() => this.handleReplicate(view)}
                            disabled={!agencyClientId}
                            title={!agencyClientId ? t('saved_views.replicate_needs_client') : t('saved_views.replicate_here_hint')}
                        >
                            {t('saved_views.action_replicate_here')}
                        </button>
                    )}
                    <button
                        type="button"
                        className="saved-views-palette-action-icon"
                        onClick={(e) => this.handleEditView(view, e)}
                        title={t('saved_views.action_rename_edit')}
                        aria-label={t('saved_views.action_rename_edit')}
                    >
                        <FaIcon iconName="pen" />
                    </button>
                    <button
                        type="button"
                        className="saved-views-palette-action-icon -danger"
                        onClick={(e) => this.handleAskDelete(view, e)}
                        title={t('saved_views.delete_view')}
                        aria-label={t('saved_views.delete_view')}
                    >
                        <FaIcon iconName="trash" />
                    </button>
                </div>
            </div>
        );
    }

    _renderClientScope() {
        const { t } = this.props;
        const { loading, views, error, search } = this.state;

        if (loading) {
            return <div className="saved-views-palette-loading"><Spinner /></div>;
        }
        if (error) {
            return <div className="saved-views-palette-empty saved-views-palette-error">{error}</div>;
        }

        const filtered = views.filter((v) => this._matchesSearch(v));
        const assigned = filtered.filter((v) => v.assigned_to_portal)
            .sort((a, b) => (a.portal_order ?? 0) - (b.portal_order ?? 0));
        const personal = filtered.filter((v) => !v.assigned_to_portal);

        // Orden de render = orden del flat para el teclado.
        this._flatViews = [...assigned, ...personal];
        let idx = 0;

        if (filtered.length === 0) {
            return (
                <div className="saved-views-palette-empty">
                    {search.trim() ? t('saved_views.palette_no_match') : t('saved_views.palette_empty')}
                </div>
            );
        }

        return (
            <>
                {assigned.length > 0 && (
                    <div className="saved-views-palette-section">
                        <div className="saved-views-palette-section-title">
                            {t('saved_views.section_assigned')}
                        </div>
                        {assigned.map((v) => this._renderViewRow(v, idx++))}
                    </div>
                )}
                {personal.length > 0 && (
                    <div className="saved-views-palette-section">
                        <div className="saved-views-palette-section-title -muted">
                            {t('saved_views.section_personal')}
                        </div>
                        {personal.map((v) => this._renderViewRow(v, idx++))}
                    </div>
                )}
            </>
        );
    }

    _renderAllScope() {
        const { t } = this.props;
        const { allLoading, allError, search } = this.state;

        if (allLoading) {
            return <div className="saved-views-palette-loading"><Spinner /></div>;
        }
        if (allError) {
            return <div className="saved-views-palette-empty saved-views-palette-error">{allError}</div>;
        }

        const groups = this._buildAllGroups();
        this._flatViews = groups.reduce((acc, g) => acc.concat(g.views), []);

        if (this._flatViews.length === 0) {
            return (
                <div className="saved-views-palette-empty">
                    {search.trim() ? t('saved_views.palette_no_match') : t('saved_views.all_empty')}
                </div>
            );
        }

        let idx = 0;
        return groups.map((group) => (
            <div className="saved-views-palette-group" key={group.clientId}>
                <div className="saved-views-palette-group-title">
                    <span className="saved-views-palette-group-name">{group.clientName}</span>
                    {group.isCurrent && (
                        <span className="saved-views-palette-group-current">
                            {t('saved_views.client_current_suffix')}
                        </span>
                    )}
                    <span className="saved-views-palette-group-count">{group.views.length}</span>
                </div>
                {group.views.map((v) => this._renderViewRow(v, idx++))}
            </div>
        ));
    }

    render() {
        const { t, isOpen, toggle, agencyClientId } = this.props;
        const { scope, search, loading, allLoading } = this.state;

        const busy = scope === SCOPE_CLIENT ? loading : allLoading;
        const searchPlaceholder = scope === SCOPE_ALL
            ? t('saved_views.palette_search_all_placeholder')
            : t('saved_views.palette_search_placeholder');

        // Reset del flat antes de renderizar el cuerpo (lo repuebla el render del scope).
        this._flatViews = [];

        return (
            <>
            <Modal isOpen={isOpen} toggle={toggle} className="saved-views-palette-modal">
                <ModalHeader toggle={toggle}>
                    {t('saved_views.palette_title')}
                </ModalHeader>
                <ModalBody>
                    <div className="modal-section">
                        <div className="saved-views-palette-scope" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={scope === SCOPE_CLIENT}
                                className={`saved-views-palette-scope-tab ${scope === SCOPE_CLIENT ? '-active' : ''}`}
                                onClick={() => this.setScope(SCOPE_CLIENT)}
                            >
                                {t('saved_views.scope_this_client')}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={scope === SCOPE_ALL}
                                className={`saved-views-palette-scope-tab ${scope === SCOPE_ALL ? '-active' : ''}`}
                                onClick={() => this.setScope(SCOPE_ALL)}
                            >
                                {t('saved_views.scope_all_clients')}
                            </button>
                        </div>

                        <div className="form-row">
                            <input
                                ref={this.inputRef}
                                type="text"
                                className="form-control"
                                placeholder={searchPlaceholder}
                                value={search}
                                onChange={(e) => this.setState({ search: e.target.value, highlightIndex: 0 })}
                                onKeyDown={this.handleKeyDown}
                                disabled={busy}
                            />
                        </div>

                        {scope === SCOPE_CLIENT && !agencyClientId && (
                            <div className="saved-views-palette-empty">
                                {t('saved_views.palette_pick_client')}
                            </div>
                        )}

                        {scope === SCOPE_ALL && (
                            <div className="saved-views-palette-scope-hint">
                                <FaIcon iconName="circle-info" />
                                {' '}{t('saved_views.all_scope_hint')}
                            </div>
                        )}

                        {scope === SCOPE_CLIENT && agencyClientId && this._renderClientScope()}
                        {scope === SCOPE_ALL && this._renderAllScope()}

                        <div className="saved-views-palette-hint">
                            {t('saved_views.palette_keyboard_hint')}
                        </div>
                    </div>
                </ModalBody>
            </Modal>

            <EditViewMetadataModal
                isOpen={!!this.state.editingView}
                toggle={() => this.setState({ editingView: null })}
                view={this.state.editingView}
                onSaved={this.handleMetadataSaved}
            />

            {this.state.deletingView && (
                <YesNoModal
                    isOpen={!!this.state.deletingView}
                    title={t('saved_views.delete_view_title')}
                    question={this.state.deletingView.assigned_to_portal
                        ? t('saved_views.delete_view_portal_warning', { name: this.state.deletingView.name })
                        : t('saved_views.delete_view_confirm', { name: this.state.deletingView.name })}
                    onYesAnswer={this.confirmDelete}
                    onNoAnswer={() => this.setState({ deletingView: null })}
                />
            )}
            </>
        );
    }
}

export const SavedViewsCommandPalette = withTranslation()(SavedViewsCommandPaletteComponent);
